<template>
  <page class="stay">
    <header-component
      :content-props="{
        hasUserMenu: true,
        hasSearch: true,
        hasDiscoverMenu: true,
        hasLibraryLink: true,
        hasHighlightsLink: true
      }"
    />
    <section class="stay__section">
      <div class="stay__container">
        <pict block-name="stay" class="p-4" :default="imageSrc" />

        <div class="stay__content container">
          <div class="stay__description" v-html="$t(`description.${churnSurveyOption}`)" />

          <button
            class="stay__button button button--midnight button--full"
            @click="handleButtonClick"
            :disabled="loading"
          >
            {{ buttonCopy }}
          </button>

          <div v-if="hasDiscount" class="stay__extra-info" v-html="$t('extraInfo')" />

          <div
            v-if="hasDiscount"
            :class="{
              'stay__cancel-link': true,
              'stay__cancel-link--disabled': loading
            }"
            @click="cancelSubscription"
          >
            {{ $t("cancel") }}
          </div>
        </div>
      </div>
    </section>
  </page>
</template>

<script>
import Page from "@/shared/page"
import HeaderComponent from "@/features/header/components"
import Pict from "@/shared/pict"
import globals from "@/utils/globals"
import api from "@/api"
import { go } from "@/utils/location"
import getLocale from "@/utils/locale"

const IMAGE_BASE_URL = "https://static.blinkist.com/churn/stay_page/"

export default {
  name: "StayPage",
  components: {
    Page,
    HeaderComponent,
    Pict
  },
  props: {
    churnSurveyOption: {
      type: String,
      default: "other"
    },
    hasDiscount: {
      type: Boolean,
      default: true
    },
    subscriptionType: {
      type: String,
      default: null
    },
    subscriptionRenewalDate: {
      type: String,
      default: ""
    }
  },
  data: () => ({
    loading: false
  }),
  image: {
    withDiscount: {
      en: {
        l: `${IMAGE_BASE_URL}discount_image_en.png`,
        l2x: `${IMAGE_BASE_URL}discount_image_en@2x.png`
      },
      de: {
        l: `${IMAGE_BASE_URL}discount_image_de.png`,
        l2x: `${IMAGE_BASE_URL}discount_image_de@2x.png`
      },
      es: {
        l: `${IMAGE_BASE_URL}discount_image_es.png`,
        l2x: `${IMAGE_BASE_URL}discount_image_es@2x.png`
      },
      pt: {
        l: `${IMAGE_BASE_URL}discount_image_pt.png`,
        l2x: `${IMAGE_BASE_URL}discount_image_pt@2x.png`
      }
    },
    noDiscount: {
      en: {
        l: `${IMAGE_BASE_URL}no_discount_image.png`,
        l2x: `${IMAGE_BASE_URL}no_discount_image@2x.png`
      },
      de: {
        l: `${IMAGE_BASE_URL}no_discount_image.png`,
        l2x: `${IMAGE_BASE_URL}no_discount_image@2x.png`
      },
      es: {
        l: `${IMAGE_BASE_URL}no_discount_image.png`,
        l2x: `${IMAGE_BASE_URL}no_discount_image@2x.png`
      },
      pt: {
        l: `${IMAGE_BASE_URL}no_discount_image.png`,
        l2x: `${IMAGE_BASE_URL}no_discount_image@2x.png`
      }
    }
  },
  computed: {
    buttonCopy() {
      return this.hasDiscount ? this.$t("getDiscount") : this.$t("cancel")
    },
    imageSrc() {
      const image = this.hasDiscount ? this.$options.image.withDiscount : this.$options.image.noDiscount

      return {
        src: image[getLocale()].l,
        srcset: {
          "2x": image[getLocale()].l2x
        }
      }
    }
  },
  methods: {
    handleButtonClick() {
      this.hasDiscount ? this.getDiscount() : this.cancelSubscription()
    },
    getDiscount() {
      if (this.loading) return

      this.loading = true

      return api.churnPrevention
        .applyChurnDiscount()
        .then(() => {
          go(`/${getLocale()}/nc/success?type=stay&sku=${this.subscriptionType}&date=${this.subscriptionRenewalDate}`)
        })
        .catch(() => {
          this.$store.dispatch("toaster/push", {
            id: "api/cancelSubscription",
            message: this.$t("apiError"),
            type: "error"
          })
        })
        .finally(() => {
          this.loading = false
        })
    },
    cancelSubscription() {
      if (this.loading) return

      this.loading = true

      return api.settings
        .cancelSubscription(this.churnSurveyOption)
        .then(() => {
          go(globals.routes.settingsPath)
        })
        .catch(() => {
          this.loading = false
          this.$store.dispatch("toaster/push", {
            id: "api/cancelSubscription",
            message: this.$t("apiError"),
            type: "error"
          })
        })
    }
  },
  i18n: {
    messages: {
      en: {
        description: {
          /* eslint-disable camelcase */
          too_expensive:
            "Sorry to hear that the subscription price doesn’t fit your budget. That's why we’d like to give you <strong>50% off right now</strong>—so you can continue to learn and grow.",
          too_short:
            "Each month we add 40 new titles, plus new Shortcasts and weekly new episodes. We’d like to offer you <strong>50% off</strong> to stay with us to take advantage of it all.",
          content:
            "Each month we add 40 new titles, plus new Shortcasts and weekly new episodes. We’d like to offer you <strong>50% off</strong> to stay with us to take advantage of it all.",
          not_using_app:
            "Reminders like our daily push messages can help! Make sure to set them up in your mobile settings. We’d also like to offer you <strong>50% off</strong> your Premium subscription to keep learning with us.",
          basic:
            "We love that you’re getting something out of the Free Daily. We'd like to offer you more, with <strong>50% off</strong> Premium so you can keep your unlimited access to our library!",
          forget:
            "Blinkist is not a subscription trap. We make cancellation easy.<br><br>Each month we add 40 new titles, plus new Shortcasts and weekly new episodes. We’d like to offer you <strong>50% off</strong> to stay with us to take advantage of it all.",
          short_trial:
            "While we don’t offer a longer trial, we’d like to give you <strong>50% off</strong> a Premium subscription so that you can experience all that Blinkist has to offer at your own pace.",
          features_are_missing:
            "We’re sorry to hear that you prefer to leave us. We take your feedback seriously, so we’d like to offer you <strong>50% off</strong> to stay with us and experience all that's in store.",
          didnt_find_content:
            "We’re always open to your feedback, and it will help us improve. Let us know at <a href='mailto:feedback@blinkist.com'>feedback@blinkist.com</a>.",
          other:
            "We’re sorry to hear that you prefer to leave us. We take your feedback seriously, so we’d like to offer you <strong>50% off</strong> to stay with us and experience all that's in store."
        } /* eslint-enable camelcase */,
        extraInfo: "The discount will be applied to your subscription with your next payment.",
        getDiscount: "Stay and get 50% off",
        cancel: "Cancel subscription",
        apiError: "Something went wrong"
      },
      de: {
        description: {
          /* eslint-disable camelcase */
          too_expensive:
            "Es tut uns leid zu hören, dass das Blinkist-Abo zu teuer für dich ist. Wie wäre es mit einem <strong>50%-Sofort-Rabatt</strong> auf dein nächstes Jahr Premium?",
          too_short:
            "Wusstest du, dass jeden Monat über 40 neue Titel hinzu kommen? Dazu bringen wir ständig neue Funktionen in die App, die es einfacher machen, Blinkist in den Alltag zu integrieren.<br><br>Sichere dir den <strong>50%-„Bleib-bei-uns” Rabatt</strong>, um keine Verbesserung zu verpassen.",
          content:
            "Wusstest du, dass jeden Monat über 40 neue Titel hinzu kommen? Dazu bringen wir ständig neue Funktionen in die App, die es einfacher machen, Blinkist in den Alltag zu integrieren.<br><br>Sichere dir den <strong>50%-„Bleib-bei-uns” Rabatt</strong>, um keine Verbesserung zu verpassen.",
          not_using_app:
            "78,9% unserer Nutzer haben mit Blinkist ihr Leben bereits zum Positiven verändert. Das gelingt am besten, indem man Blinkist in den Alltag integriert. Dafür haben wir in letzter Zeit viele praktische Funktionen in die App gebracht.<br><br>Neugierig? Sichere dir den <strong>50%-„Bleib-bei-uns” Rabatt</strong> und genieße ein weiteres Jahr Premium.",
          basic:
            "Bist du dir sicher? Wir sind auch Fans des Blink des Tages, aber was gibt es schöneres, als aus 27 Kategorien genau den Titel auszuwählen, auf den man gerade Lust hat?<br><br>Vielleicht ein <strong>50%-„Bleib-bei-uns” Rabatt</strong> für ein weiteres Jahr Blinkist Premium zum Vorteilspreis?",
          forget:
            "Blinkist ist keine Abofalle. Wir machen die Kündigung einfach.<br><br>Wusstest du, dass jeden Monat über 40 neue Titel hinzu kommen? Dazu bringen wir ständig neue Funktionen in die App, die es einfacher machen, Blinkist in den Alltag zu integrieren.<br><br>Sichere dir den <strong>50%-„Bleib-bei-uns” Rabatt</strong>, um keine Verbesserung zu verpassen.",
          short_trial:
            "Ein längeres Probeabo können wir leider nicht anbieten, aber wie wäre es mit einem <strong>50%-Rabatt</strong> für ein ganzes Jahr Premium? Umgerechnet schenken wir dir damit sogar 4 Monate!",
          features_are_missing:
            "Wusstest du, dass jeden Monat über 40 neue Titel hinzu kommen? Dazu bringen wir ständig neue Funktionen in die App, die es einfacher machen, Blinkist in den Alltag zu integrieren.<br><br>Sichere dir den <strong>50%-„Bleib-bei-uns” Rabatt</strong>, um keine Verbesserung zu verpassen.",
          didnt_find_content:
            "Wusstest du, dass jeden Monat über 40 neue Titel hinzu kommen? Dazu bringen wir ständig neue Funktionen in die App, die es einfacher machen, Blinkist in den Alltag zu integrieren.<br><br>Sichere dir den <strong>50%-„Bleib-bei-uns” Rabatt</strong>, um keine Verbesserung zu verpassen.",
          other:
            "Schade, dass du dein Abo kündigen möchtest! Können wir dich umstimmen? Vielleicht mit einem <strong>50%-„Bleib-bei-uns” Rabatt</strong>?"
        } /* eslint-enable camelcase */,
        extraInfo: "Der Rabatt wird auf deine nächste Rechnung angewandt.",
        getDiscount: "50%-Rabatt einlösen",
        cancel: "Trotzdem kündigen",
        apiError: "Das hat leider nicht geklappt."
      },
      es: {
        description: {
          /* eslint-disable camelcase */
          too_expensive: "Lamentamos que el precio de la suscripción no se ajuste a tu presupuesto. Por eso, nos gustaría ofrecerte un <strong>descuento del 50 % ahora mismo</strong> para que puedas seguir aprendiendo y creciendo.",
          too_short: "Cada mes añadimos 40 títulos nuevos, además de nuevos Shortcasts y episodios semanales. Nos gustaría ofrecerte un <strong>50 % de descuento</strong> para que te quedes con nosotros y aproveches todo el contenido.",
          content: "Cada mes añadimos 40 títulos nuevos, además de nuevos Shortcasts y episodios semanales. Nos gustaría ofrecerte un <strong>50 % de descuento</strong> para que te quedes con nosotros y aproveches todo el contenido.",
          not_using_app: "¡Los recordatorios, como nuestras notificaciones push diarias, pueden ayudarte! Asegúrate de configurarlos en los ajustes de tu móvil. También nos gustaría ofrecerte un <strong>50 % de descuento</strong> en tu suscripción Premium para que sigas aprendiendo con nosotros.",
          basic: "Nos encanta que le saques partido al plan gratuito diario. ¡Nos gustaría ofrecerte más, con un <strong>50 % de descuento</strong> en Premium para que mantengas tu acceso ilimitado a nuestra biblioteca!",
          forget: "Blinkist no es una suscripción trampa. Hacemos que la cancelación sea fácil.<br><br>Cada mes añadimos 40 títulos nuevos, además de nuevos Shortcasts y episodios semanales. Nos gustaría ofrecerte un <strong>50 % de descuento</strong> para que te quedes con nosotros y aproveches todo el contenido.",
          short_trial: "Aunque no ofrecemos un período de prueba más largo, nos gustaría darte un <strong>50 % de descuento</strong> en la suscripción Premium para que puedas experimentar todo lo que Blinkist te ofrece a tu propio ritmo.",
          features_are_missing: "Lamentamos que prefieras dejarnos. Nos tomamos muy en serio tus comentarios, por lo que nos gustaría ofrecerte un <strong>50 % de descuento</strong> para que te quedes con nosotros y experimentes todo lo que está por venir.",
          didnt_find_content: "Siempre estamos abiertos a tus comentarios, nos ayudarán a mejorar. Escríbenos a <a href='mailto:feedback@blinkist.com'>feedback@blinkist.com</a>.",
          other: "Lamentamos que prefieras dejarnos. Nos tomamos muy en serio tus comentarios, por lo que nos gustaría ofrecerte un <strong>50 % de descuento</strong> para que te quedes con nosotros y experimentes todo lo que está por venir."
          /* eslint-enable camelcase */
        },
        extraInfo: "El descuento se aplicará a tu suscripción con tu próximo pago.",
        getDiscount: "Quédate y obtén un 50 % de descuento",
        cancel: "Cancelar suscripción",
        apiError: "Algo ha ido mal"
      },
      pt: {
        description: {
          /* eslint-disable camelcase */
          too_expensive: "Lamentamos saber que o preço da assinatura não se ajusta ao seu orçamento. É por isso que gostaríamos de lhe dar <strong>50% de desconto agora mesmo</strong> — para que você possa continuar a aprender e a crescer.",
          too_short: "A cada mês, adicionamos 40 novos títulos, além de novos Shortcasts e episódios semanais. Gostaríamos de lhe oferecer <strong>50% de desconto</strong> para ficar conosco e aproveitar tudo isso.",
          content: "A cada mês, adicionamos 40 novos títulos, além de novos Shortcasts e episódios semanais. Gostaríamos de lhe oferecer <strong>50% de desconto</strong> para ficar conosco e aproveitar tudo isso.",
          not_using_app: "Lembretes como nossas mensagens push diárias podem ajudar! Certifique-se de configurá-los nas definições do seu celular. Também gostaríamos de lhe oferecer <strong>50% de desconto</strong> na sua assinatura Premium para continuar aprendendo conosco.",
          basic: "Adoramos que você esteja aproveitando o Free Daily. Gostaríamos de lhe oferecer mais, com <strong>50% de desconto</strong> no Premium para que você possa manter seu acesso ilimitado à nossa biblioteca!",
          forget: "O Blinkist não é uma armadilha de assinatura. Facilitamos o cancelamento.<br><br>A cada mês, adicionamos 40 novos títulos, além de novos Shortcasts e episódios semanais. Gostaríamos de lhe oferecer <strong>50% de desconto</strong> para ficar conosco e aproveitar tudo isso.",
          short_trial: "Embora não ofereçamos um período de teste mais longo, gostaríamos de lhe dar <strong>50% de desconto</strong> em uma assinatura Premium para que você possa experimentar tudo o que o Blinkist tem a oferecer no seu próprio ritmo.",
          features_are_missing: "Lamentamos saber que você prefere nos deixar. Levamos seu feedback a sério, por isso gostaríamos de lhe oferecer <strong>50% de desconto</strong> para ficar conosco e experimentar tudo o que está por vir.",
          didnt_find_content: "Estamos sempre abertos ao seu feedback, e ele nos ajudará a melhorar. Envie-nos uma mensagem para <a href='mailto:feedback@blinkist.com'>feedback@blinkist.com</a>.",
          other: "Lamentamos saber que você prefere nos deixar. Levamos seu feedback a sério, por isso gostaríamos de lhe oferecer <strong>50% de desconto</strong> para ficar conosco e experimentar tudo o que está por vir."
          /* eslint-enable camelcase */
        },
        extraInfo: "O desconto será aplicado à sua assinatura no próximo pagamento.",
        getDiscount: "Fique e ganhe 50% de desconto",
        cancel: "Cancelar assinatura",
        apiError: "Algo correu mal"
      }
    }
  }
}
</script>

<style lang="scss">
.stay {
  &__section {
    padding-bottom: $spacing-48;

    @include media(min-width, m) {
      padding: $spacing-64 0;
    }
  }

  &__container {
    display: flex;
    flex-direction: column;
    align-items: center;

    max-width: 1024px;

    @include media(min-width, m) {
      flex-direction: row-reverse;
      margin-right: auto;
      margin-left: auto;
    }
  }

  &__picture {
    width: 100%;

    @include media(min-width, m) {
      width: 80%;
    }
  }

  &__image {
    width: 100%;

    margin-bottom: $spacing-24;

    @include media(min-width, m) {
      width: auto;
      height: 100%;

      margin-bottom: 0;
    }
  }

  &__content {
    display: flex;
    flex-direction: column;
    align-items: center;

    @include media(min-width, m) {
      align-items: flex-start;

      padding-right: $spacing-32;
    }
  }

  &__description {
    @include font-style(p1, $color: $dark-grey);

    margin-bottom: $spacing-32;
  }

  &__button {
    max-width: rem(296px);

    margin-bottom: $spacing-16;

    text-align: center;

    a {
      color: $white;

      background: $midnight;
    }

    @include media(min-width, m) {
      text-align: left;
    }
  }

  &__extra-info {
    @include font-style(caption, $color: $mid-grey);

    max-width: rem(296px);

    margin-bottom: $spacing-16;

    text-align: center;

    @include media(min-width, m) {
      text-align: left;
    }
  }

  &__cancel-link {
    @include font-style(button, $color: $raspberry);

    font-weight: bold;

    text-align: center;

    text-decoration: underline;

    cursor: pointer;

    &:hover {
      color: $raspberry2;
    }
  }

  &__cancel-link--disabled {
    color: $light-grey;

    text-decoration: none;

    cursor: default;

    &:hover {
      color: $light-grey;
    }
  }
}
</style>
